import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useEcommerce } from '../../context/EcommerceContext';

const {
  FiBell, FiCheckCircle, FiXCircle, FiInfo, FiTrash2,
  FiX, FiCheck, FiHeart, FiSun, FiGift, FiFilter,
  FiUsers, FiCalendar, FiLink, FiMessageCircle,
} = FiIcons;

const typeConfig = {
  tribute:     { icon: FiHeart,          bg: 'bg-rose-50',    color: 'text-rose-500',    label: 'Tribute'     },
  candle:      { icon: FiSun,            bg: 'bg-amber-50',   color: 'text-amber-500',   label: 'Candle'      },
  success:     { icon: FiCheckCircle,    bg: 'bg-green-50',   color: 'text-green-600',   label: 'Success'     },
  failure:     { icon: FiXCircle,        bg: 'bg-red-50',     color: 'text-red-500',     label: 'Alert'       },
  info:        { icon: FiInfo,           bg: 'bg-blue-50',    color: 'text-blue-500',    label: 'Info'        },
  group_buy:   { icon: FiGift,           bg: 'bg-purple-50',  color: 'text-purple-500',  label: 'Group Buy'   },
  family_tree: { icon: FiUsers,          bg: 'bg-teal-50',    color: 'text-teal-600',    label: 'Family'      },
  life_event:  { icon: FiCalendar,       bg: 'bg-indigo-50',  color: 'text-indigo-600',  label: 'Life Event'  },
  tree_link:   { icon: FiLink,           bg: 'bg-emerald-50', color: 'text-emerald-600', label: 'Tree Link'   },
  chat:        { icon: FiMessageCircle,  bg: 'bg-sky-50',     color: 'text-sky-600',     label: 'Chat'        },
};

const getTypeConfig = (type) => typeConfig[type] || typeConfig.info;

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m    = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── Single Notification Row ───────────────────────────────────────────────────
function NotificationItem({ notif, onMarkRead, onDelete }) {
  const cfg = getTypeConfig(notif.type);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 40, scale: 0.95 }}
      className={`relative group p-4 rounded-2xl border transition-all ${
        notif.is_read
          ? 'bg-gray-50 border-gray-100 opacity-70'
          : 'bg-white border-accent-navy/10 shadow-sm'
      }`}
    >
      {!notif.is_read && (
        <span className="absolute top-4 right-4 w-2 h-2 rounded-full bg-accent-gold" />
      )}

      <div className="flex gap-3 pr-4">
        <div className={`mt-0.5 w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
          <SafeIcon icon={cfg.icon} className={`w-4 h-4 ${cfg.color}`} />
        </div>

        <div className="flex-1 min-w-0">
          <p className={`text-sm font-bold leading-snug mb-0.5 ${notif.is_read ? 'text-gray-600' : 'text-accent-navy'}`}>
            {notif.title}
          </p>
          <p className="text-xs text-text-muted leading-relaxed line-clamp-2">{notif.message}</p>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-[10px] text-text-muted font-medium">{timeAgo(notif.created_at)}</span>
            <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
              {cfg.label}
            </span>
            {!notif.is_read && (
              <button
                onClick={() => onMarkRead(notif.id)}
                className="text-[10px] font-bold text-accent-gold hover:text-accent-navy transition-colors flex items-center gap-1"
              >
                <SafeIcon icon={FiCheck} className="w-3 h-3" /> Mark read
              </button>
            )}
          </div>
        </div>
      </div>

      <button
        onClick={() => onDelete(notif.id)}
        className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 rounded-full bg-red-50 text-red-400 hover:bg-red-100 flex items-center justify-center"
      >
        <SafeIcon icon={FiX} className="w-3 h-3" />
      </button>
    </motion.div>
  );
}

const FILTERS = ['all', 'unread', 'chat', 'family_tree', 'tribute', 'success', 'failure'];
const FILTER_LABELS = {
  all:         n  => `All (${n})`,
  unread:      () => 'Unread',
  chat:        () => '💬 Chat',
  family_tree: () => 'Family',
  tribute:     () => 'Tribute',
  success:     () => 'Success',
  failure:     () => 'Alerts',
};

// ── Chat Log Sub-Panel ────────────────────────────────────────────────────────
function ChatLogPanel({ notifications }) {
  const chatNotifs = notifications.filter(n => n.type === 'chat');
  if (chatNotifs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-center px-4">
        <div className="w-14 h-14 rounded-full bg-sky-50 flex items-center justify-center mb-3">
          <SafeIcon icon={FiMessageCircle} className="w-6 h-6 text-sky-400" />
        </div>
        <p className="text-sm font-bold text-accent-navy mb-1">No chat logs yet</p>
        <p className="text-xs text-text-muted">Messages sent to family members will appear here.</p>
      </div>
    );
  }

  // Group by recipient
  const grouped = chatNotifs.reduce((acc, n) => {
    const recipient = n.title.replace('💬 Message sent to ', '') || 'Unknown';
    if (!acc[recipient]) acc[recipient] = [];
    acc[recipient].push(n);
    return acc;
  }, {});

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-2 px-1">
        <SafeIcon icon={FiMessageCircle} className="w-4 h-4 text-sky-500" />
        <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">
          {chatNotifs.length} message{chatNotifs.length !== 1 ? 's' : ''} sent
        </p>
      </div>
      {Object.entries(grouped).map(([recipient, msgs]) => (
        <div key={recipient} className="bg-white rounded-2xl border border-brand-dark overflow-hidden shadow-sm">
          <div className="flex items-center gap-3 px-4 py-3 bg-sky-50 border-b border-sky-100">
            <div className="w-8 h-8 rounded-xl bg-accent-navy flex items-center justify-center text-accent-gold font-black text-sm">
              {recipient[0]}
            </div>
            <div>
              <p className="font-bold text-accent-navy text-sm">{recipient}</p>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">
                {msgs.length} message{msgs.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="divide-y divide-brand-light">
            {msgs.slice(0, 5).map(m => (
              <div key={m.id} className="px-4 py-2.5 flex items-start gap-2">
                <SafeIcon icon={FiMessageCircle} className="w-3 h-3 text-sky-400 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-accent-navy leading-relaxed truncate">{m.message}</p>
                  <p className="text-[9px] text-text-muted mt-0.5">{timeAgo(m.created_at)}</p>
                </div>
              </div>
            ))}
            {msgs.length > 5 && (
              <div className="px-4 py-2 text-center text-[9px] font-black text-text-muted uppercase tracking-widest">
                +{msgs.length - 5} more messages
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────
const NotificationCenter = ({ isOpen, onClose }) => {
  const { notifications, markNotificationAsRead, deleteNotification, clearAllNotifications } = useEcommerce();
  const [activeFilter,     setActiveFilter]     = useState('all');
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const chatCount   = notifications.filter(n => n.type === 'chat').length;

  const filtered = notifications.filter(n => {
    if (activeFilter === 'all')    return true;
    if (activeFilter === 'unread') return !n.is_read;
    return n.type === activeFilter;
  });

  const handleMarkAll = () =>
    notifications.filter(n => !n.is_read).forEach(n => markNotificationAsRead(n.id));

  const handleClearAll = () => {
    clearAllNotifications();
    setShowClearConfirm(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100]"
          />

          <motion.div
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-[#FDFCF9] shadow-2xl z-[101] flex flex-col"
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-brand-dark bg-white flex-shrink-0">
              <div className="flex items-start justify-between mb-1">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-2xl bg-accent-gold/10 flex items-center justify-center">
                      <SafeIcon icon={FiBell} className="w-5 h-5 text-accent-gold" />
                    </div>
                    {unreadCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center border-2 border-white">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </div>
                  <div>
                    <h2 className="text-lg font-black text-accent-navy tracking-tight">Notifications</h2>
                    <p className="text-[11px] text-text-muted font-medium">
                      {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
                      {chatCount > 0 && (
                        <span className="ml-2 text-sky-600">· {chatCount} chat log{chatCount !== 1 ? 's' : ''}</span>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-brand-light hover:bg-red-50 hover:text-red-500 text-text-muted transition-all border border-brand-dark"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAll}
                  className="mt-3 text-[10px] font-black uppercase tracking-widest text-accent-gold hover:text-accent-navy transition-colors flex items-center gap-1.5"
                >
                  <SafeIcon icon={FiCheck} className="w-3 h-3" /> Mark all as read
                </button>
              )}
            </div>

            {/* Filters */}
            <div className="px-6 py-3 border-b border-brand-dark bg-white flex-shrink-0">
              <div className="flex items-center gap-1 overflow-x-auto hide-scrollbar">
                <SafeIcon icon={FiFilter} className="w-3 h-3 text-text-muted flex-shrink-0 mr-1" />
                {FILTERS.map(f => (
                  <button
                    key={f}
                    onClick={() => setActiveFilter(f)}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-all flex-shrink-0 ${
                      activeFilter === f
                        ? f === 'chat'
                          ? 'bg-sky-600 text-white'
                          : 'bg-accent-navy text-white'
                        : 'bg-brand-light text-text-muted hover:text-accent-navy'
                    }`}
                  >
                    {FILTER_LABELS[f]?.(notifications.length) ?? f}
                    {f === 'chat' && chatCount > 0 && (
                      <span className={`ml-1.5 w-4 h-4 inline-flex items-center justify-center rounded-full text-[8px] font-black ${
                        activeFilter === 'chat' ? 'bg-white text-sky-600' : 'bg-sky-600 text-white'
                      }`}>
                        {chatCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* List / Chat Log */}
            <div className="flex-1 overflow-y-auto">
              {activeFilter === 'chat' ? (
                <ChatLogPanel notifications={notifications} />
              ) : (
                <div className="p-4 space-y-3">
                  <AnimatePresence mode="popLayout">
                    {filtered.length === 0 ? (
                      <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        className="flex flex-col items-center justify-center h-64 text-center"
                      >
                        <div className="w-16 h-16 rounded-full bg-brand-light flex items-center justify-center mb-4">
                          <SafeIcon icon={FiBell} className="w-7 h-7 text-text-muted/30" />
                        </div>
                        <p className="text-sm font-bold text-accent-navy mb-1">
                          {activeFilter === 'all' ? 'No notifications yet' : `No ${activeFilter} notifications`}
                        </p>
                        <p className="text-xs text-text-muted max-w-[200px]">
                          {activeFilter === 'all'
                            ? 'Family tree changes, tributes, and chat messages will appear here.'
                            : 'Try a different filter to see more.'}
                        </p>
                      </motion.div>
                    ) : (
                      filtered.map(notif => (
                        <NotificationItem
                          key={notif.id}
                          notif={notif}
                          onMarkRead={markNotificationAsRead}
                          onDelete={deleteNotification}
                        />
                      ))
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="p-4 border-t border-brand-dark bg-white flex-shrink-0">
                {!showClearConfirm ? (
                  <button
                    onClick={() => setShowClearConfirm(true)}
                    className="w-full py-3 text-xs font-black uppercase tracking-widest text-text-muted hover:text-red-500 transition-colors flex items-center justify-center gap-2 rounded-2xl hover:bg-red-50"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" /> Clear all notifications
                  </button>
                ) : (
                  <div className="space-y-2">
                    <p className="text-center text-xs font-bold text-accent-navy">Clear all {notifications.length} notifications?</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowClearConfirm(false)}
                        className="flex-1 py-2.5 rounded-xl border border-brand-dark text-xs font-bold text-text-muted hover:bg-brand-light transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleClearAll}
                        className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-xs font-black uppercase tracking-wider hover:bg-red-600 transition-colors"
                      >
                        Yes, Clear All
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NotificationCenter;